import React, { createContext, useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import AppLegalFooter from './components/AppLegalFooter';
import Header from './components/Header';
import FloatingContact from './components/FloatingContact';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Register from './pages/Register';
import ProfileSetup from './pages/Onboarding';
import ContactFlow from './pages/ContactFlow';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import PublicStop from './pages/PublicStop';
import Workplace from './pages/Workplace';
import HomeVisit from './pages/HomeVisit';
import KnowYourRights from './pages/KnowYourRights';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import {
  DEFAULT_LANGUAGE,
  getStoredLanguage,
  isSupportedLanguage,
  persistLanguage,
  getUrlLanguage,
} from './utils/language';
import { translations } from './utils/translations';

export const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
});

class ErrorBoundary extends React.Component {
  static contextType = LanguageContext;

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const language = this.context?.language;
      const t = translations[language] || translations.en;

      return (
        <div className="App">
          <Header />
          <div className="page-container" style={{ padding: '40px', textAlign: 'center' }}>
            <h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>{t.system.errorTitle}</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>
              {t.system.errorBody}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#C9A24A',
                color: '#0B1F3A',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              {t.system.refreshPage}
            </button>
          </div>
          <FloatingContact userData={null} />
        </div>
      );
    }

    return this.props.children;
  }
}

function ProfileSetupRoute({ children }) {
  const { user, intakeComplete, loading } = useAuth();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isGuest = params.get('guest') === 'true';

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p>{t.system.loadingSecureAccess}</p>
        </div>
      </div>
    );
  }

  if (!user && !isGuest) {
    return <Navigate to="/register" replace />;
  }

  if (user && intakeComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AuthEntryRoute({ children }) {
  const { user, intakeComplete, loading } = useAuth();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p>{t.system.loadingSecureAccess}</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={intakeComplete ? '/dashboard' : '/intake'} replace />;
  }

  return children;
}

function ContactFlowRoute({ children }) {
  const { user, intakeComplete, loading } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isGuest = params.get('guest') === 'true';

  if (loading) {
    return null;
  }

  if (!user && !isGuest) {
    return <Navigate to="/register" replace />;
  }

  if (user && !intakeComplete) {
    return <Navigate to="/intake?redirect=contact" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const hideFloatingContact = ['/intake', '/register', '/profile-setup', '/onboarding'].includes(
    location.pathname
  );

  return (
    <ErrorBoundary>
      <div className="App">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/login"
            element={
              <AuthEntryRoute>
                <Login />
              </AuthEntryRoute>
            }
          />
          <Route
            path="/register"
            element={
              <AuthEntryRoute>
                <Register />
              </AuthEntryRoute>
            }
          />
          <Route
            path="/intake"
            element={
              <ProfileSetupRoute>
                <ProfileSetup />
              </ProfileSetupRoute>
            }
          />
          <Route path="/profile-setup" element={<Navigate to="/intake" replace />} />
          <Route path="/onboarding" element={<Navigate to="/intake" replace />} />
          <Route
            path="/contact-flow"
            element={
              <ContactFlowRoute>
                <ContactFlow />
              </ContactFlowRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <KnowYourRights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/public-stop"
            element={
              <ProtectedRoute>
                <PublicStop />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workplace"
            element={
              <ProtectedRoute>
                <Workplace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomeVisit />
              </ProtectedRoute>
            }
          />
          <Route path="/know-your-rights" element={<Navigate to="/dashboard" replace />} />
          <Route path="/scenario/public" element={<Navigate to="/dashboard" replace />} />
          <Route path="/scenario/workplace" element={<Navigate to="/dashboard" replace />} />
          <Route path="/scenario/home" element={<Navigate to="/dashboard" replace />} />
          <Route path="/rights" element={<Navigate to="/dashboard" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {location.pathname !== '/' && <AppLegalFooter />}
        {!hideFloatingContact && <FloatingContact userData={user} />}
      </div>
    </ErrorBoundary>
  );
}

function App() {
  const [language, setLanguage] = useState(() => getStoredLanguage());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const urlLanguage = getUrlLanguage();

    if (!urlLanguage) {
      return;
    }

    setLanguage(urlLanguage);

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('lang');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  }, []);

  useEffect(() => {
    const nextLanguage = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
    persistLanguage(nextLanguage);

    if (typeof document !== 'undefined') {
      document.documentElement.lang = nextLanguage;
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      <AppRoutes />
    </LanguageContext.Provider>
  );
}

export default App;
